import { Box } from '../../Box';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { Button } from '../../Button';
import { OptionButtons } from './OptionButtons';
import { useMemo } from 'react';
import { idBuilder } from '../../internal';
import { clsx } from 'clsx';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const resolveBackgroundBorder = (theme, overflow) => {
  if (overflow) {
    return {
      borderTop: `1px solid ${theme.tokens.SemColorBorderMuted}`,
      backgroundColor: 'rgba(244, 244, 244, 0.96)'
    };
  }
  return {
    backgroundColor: theme.tokens.SemColorSurfaceDefault
  };
};
const containerStyle = (theme, overflow) => ({
  display: 'flex',
  padding: overflow ? '8px 16px' : '0',
  ...resolveBackgroundBorder(theme, overflow)
});
const fluidStyle = {
  gap: '8px'
};
const splitStyle = {
  gap: '16px'
};
const rightAlignedStyle = {
  gap: '16px',
  justifyContent: 'flex-end'
};
const stackedStyle = {
  gap: '8px',
  flexDirection: 'column-reverse'
};
const resolveStyle = layout => {
  switch (layout) {
    case 'stacked':
      {
        return stackedStyle;
      }
    case 'fluid':
      {
        return fluidStyle;
      }
    case 'split':
      {
        return splitStyle;
      }
    default:
      {
        return rightAlignedStyle;
      }
  }
};
const resolveOptions = options => {
  if (!options) {
    return [];
  }
  if (Array.isArray(options)) {
    return options;
  }
  return [options];
};
export const ActionButtons = props => {
  const theme = useInterstateTheme();
  const {
    size,
    overflow,
    primary,
    options,
    layout = 'rightAligned',
    id,
    customIdentifiers = {},
    ...rest
  } = props;
  const currentOptions = useMemo(() => resolveOptions(options), [options]);
  // don't display if there isn't a primary
  if (!primary) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('ActionButtons: A primary button is required');
    }
    return null;
  }
  const style = resolveStyle(layout);
  const effectiveStyle = mergeSxStyle(containerStyle(theme, overflow), style);
  const effectiveButtonSize = size === 'small' ? 'small' : 'medium';
  return /*#__PURE__*/_jsxs(Box, {
    sx: effectiveStyle,
    id: customIdentifiers['actionsRoot']?.id || idBuilder('ids-action-buttons-container', id),
    className: clsx([idBuilder('ids-action-buttons-container', layout)]),
    "data-testid": customIdentifiers['actionsRoot']?.dataTestId || idBuilder('ids-action-buttons-container', rest['data-testid']),
    children: [options && layout === 'fluid' && currentOptions && currentOptions[0] && /*#__PURE__*/_jsx(Button, {
      buttonStyle: currentOptions[0].buttonStyle || 'secondary',
      onClick: currentOptions[0].action,
      block: true,
      size: effectiveButtonSize,
      className: 'ids-action-button-option',
      id: currentOptions[0].customIdentifiers && currentOptions[0].customIdentifiers['optionButton0']?.id || idBuilder('ids-action-button-option0', id),
      "data-testid": currentOptions[0].customIdentifiers && currentOptions[0].customIdentifiers['optionButton0']?.dataTestId || idBuilder('ids-action-button-option0', rest['data-testid']),
      disabled: currentOptions[0].disabled,
      isLoading: currentOptions[0].isLoading,
      children: currentOptions[0].label
    }), options && layout === 'fluid' && currentOptions && currentOptions[1] && /*#__PURE__*/_jsx(Button, {
      buttonStyle: currentOptions[1].buttonStyle || 'secondary',
      onClick: currentOptions[1].action,
      block: true,
      size: effectiveButtonSize,
      className: 'ids-action-button-option',
      id: currentOptions[1].customIdentifiers && currentOptions[1].customIdentifiers['optionButton1']?.id || idBuilder('ids-action-button-option1', id),
      disabled: currentOptions[1].disabled,
      isLoading: currentOptions[1].isLoading,
      "data-testid": currentOptions[1].customIdentifiers && currentOptions[1].customIdentifiers['optionButton1']?.dataTestId || idBuilder('ids-action-button-option1', rest['data-testid']),
      children: currentOptions[1].label
    }), options && layout !== 'fluid' && /*#__PURE__*/_jsx(OptionButtons, {
      optionButtons: currentOptions,
      layout: layout,
      size: effectiveButtonSize,
      id: id,
      "data-testid": rest['data-testid'],
      customIdentifiers: customIdentifiers
    }), primary && /*#__PURE__*/_jsx(Button, {
      buttonStyle: primary.buttonStyle || 'primary',
      onClick: primary.action,
      block: layout === 'fluid',
      size: effectiveButtonSize,
      className: 'ids-action-button-primary',
      id: primary.customIdentifiers && primary.customIdentifiers['primaryButton']?.id || idBuilder('ids-action-button-primary', id),
      disabled: primary.disabled,
      isLoading: primary.isLoading,
      "data-testid": primary.customIdentifiers && primary.customIdentifiers['primaryButton']?.dataTestId || idBuilder('ids-action-button-primary', rest['data-testid']),
      children: primary.label
    })]
  });
};